import { Tooltip } from "@/components/TooltipV2";
import { DurationStatus } from "@/components/data-display/DurationInfo";
import { Icon } from "@/components/icons/Icon";
import { type StatusBadgeV2Tone } from "@/components/interactive/StatusBadgeV2";
import { JobStatus, JobStatusIcon, getJobStatusTextShort } from "@/components/query/JobStatusIcon";
import { OfflineQueryStatus } from "@/gen_src/proto/chalk/server/v1/offline_queries_pb";
import { compileOnlyAssertNever } from "@/utils/asserts";
import { cx } from "@/utils/classnames";
import React from "react";

export interface QueryStatusIconProps {
  hasErrors: boolean | undefined;
  className?: string;
}

export const QueryStatusIcon: React.FC<QueryStatusIconProps> = ({ hasErrors, className }) => {
  return hasErrors == null ? null : hasErrors ? (
    <Tooltip placement={"top"} content={`This query encountered errors`} instant>
      <div>
        <Icon name="error" className={cx("!text-red-500", className)} />
      </div>
    </Tooltip>
  ) : (
    <Icon name="check" className={cx("!text-cds-bright-green-500", className)} />
  );
};

export const getTextColorClassNameForStatusProto = (
  status: OfflineQueryStatus | undefined,
  hasErrors: boolean | undefined,
) => {
  if (status != null) {
    switch (status) {
      case OfflineQueryStatus.COMPLETED:
        return "!text-cds-bright-green-500";
      case OfflineQueryStatus.FAILED:
        return "!text-red-500";
      case OfflineQueryStatus.QUEUED:
        return "text-cds-gray-400 dark:!text-cds-gray-600";
      case OfflineQueryStatus.WORKING:
        return "!text-yellow-500";
      case OfflineQueryStatus.CANCELED:
        return "!text-zinc-500";
      case OfflineQueryStatus.UNKNOWN:
      case OfflineQueryStatus.UNSPECIFIED:
        return "text-cds-gray-400 dark:!text-cds-gray-600";
      default:
        compileOnlyAssertNever(status);
        return "text-cds-gray-400 dark:!text-cds-gray-600";
    }
  }

  return hasErrors ? "!text-red-500" : "!text-cds-bright-green-500";
};

export const getBGColorClassNameForStatusProto = (
  status: OfflineQueryStatus | undefined,
  hasErrors: boolean | undefined,
) => {
  if (status != null) {
    switch (status) {
      case OfflineQueryStatus.COMPLETED:
        return "bg-cds-bright-green-500";
      case OfflineQueryStatus.FAILED:
        return "bg-red-500";
      case OfflineQueryStatus.QUEUED:
        return "bg-cds-gray-400";
      case OfflineQueryStatus.WORKING:
        return "bg-yellow-500";
      case OfflineQueryStatus.CANCELED:
        return "bg-zinc-500";
      case OfflineQueryStatus.UNKNOWN:
      case OfflineQueryStatus.UNSPECIFIED:
        return "bg-cds-gray-400";
      default:
        compileOnlyAssertNever(status);
        return "bg-cds-gray-400";
    }
  }

  return hasErrors ? "bg-red-500" : "bg-cds-bright-green-500";
};

export const getStatusBadgeToneForOfflineQueryStatusProto = (
  status: OfflineQueryStatus | undefined,
  hasErrors: boolean | undefined,
): StatusBadgeV2Tone => {
  if (status != null) {
    switch (status) {
      case OfflineQueryStatus.COMPLETED:
        return "green";
      case OfflineQueryStatus.FAILED:
        return "red";
      case OfflineQueryStatus.WORKING:
        return "yellow";
      case OfflineQueryStatus.QUEUED:
        return "blue";
      case OfflineQueryStatus.CANCELED:
      case OfflineQueryStatus.UNKNOWN:
      case OfflineQueryStatus.UNSPECIFIED:
        return "neutral";
      default:
        compileOnlyAssertNever(status);
        return "neutral";
    }
  }

  return hasErrors ? "red" : "green";
};

export interface OfflineQueryStatusIconPropsProto {
  className?: string;
  hasErrors: boolean | undefined;
  status: OfflineQueryStatus | undefined;
}

export const offlineStatusToDurationStatus = (
  status: OfflineQueryStatus | undefined,
  hasErrors: boolean | undefined,
): DurationStatus => {
  switch (status) {
    case OfflineQueryStatus.COMPLETED:
      return "success";
    case OfflineQueryStatus.WORKING:
      return "pending";
    case OfflineQueryStatus.FAILED:
    case OfflineQueryStatus.CANCELED:
      return "error";
    case OfflineQueryStatus.QUEUED:
    case OfflineQueryStatus.UNKNOWN:
    case OfflineQueryStatus.UNSPECIFIED:
      return "not-started";
    case undefined:
      return hasErrors ? "error" : "success";
    default:
      compileOnlyAssertNever(status);
      return "not-started";
  }
};

export const OfflineQueryStatusIconProto: React.FC<OfflineQueryStatusIconPropsProto> = ({
  status,
  className,
  hasErrors,
}) => {
  const fullClassName = cx(getTextColorClassNameForStatusProto(status, hasErrors), className);
  if (status != null) {
    const label = offlineQueryStatusTextProto(status, hasErrors);
    const props = {
      "aria-label": label,
      className: fullClassName,
    };
    switch (status) {
      case OfflineQueryStatus.COMPLETED:
        return <Icon name="check" {...props} />;
      case OfflineQueryStatus.FAILED:
        return <Icon name="error" {...props} />;
      case OfflineQueryStatus.QUEUED:
        return <Icon name="circle" {...props} />;
      case OfflineQueryStatus.WORKING:
        return <Icon name="clock" {...props} />;
      case OfflineQueryStatus.CANCELED:
        return <Icon name="error" {...props} />;
      case OfflineQueryStatus.UNKNOWN:
      case OfflineQueryStatus.UNSPECIFIED:
        return <Icon name="circle" {...props} />;
      default:
        compileOnlyAssertNever(status);
        return <Icon name="circle" {...props} />;
    }
  } else if (hasErrors) {
    return <Icon name="error" className={fullClassName} />;
  } else {
    return <Icon name="check" className={fullClassName} />;
  }
};

export function offlineQueryStatusTextProto(
  status: OfflineQueryStatus | undefined,
  hasErrors: boolean | undefined,
): string {
  if (status != null) {
    switch (status) {
      case OfflineQueryStatus.COMPLETED:
        return "Completed";
      case OfflineQueryStatus.FAILED:
        return "Failed";
      case OfflineQueryStatus.QUEUED:
        return "Queued";
      case OfflineQueryStatus.WORKING:
        return "Working";
      case OfflineQueryStatus.CANCELED:
        return "Canceled";
      case OfflineQueryStatus.UNKNOWN:
      case OfflineQueryStatus.UNSPECIFIED:
        return "Unknown";
      default:
        compileOnlyAssertNever(status);
        return "Unknown";
    }
  } else if (hasErrors) {
    return "Errors";
  } else {
    return "Completed";
  }
}

export interface JobStatusPillProps {
  status: JobStatus;
  hasErrors: boolean | undefined;
}

export const JobStatusPill: React.FC<JobStatusPillProps> = ({ status, hasErrors }) => {
  return (
    <div className={"flex items-center gap-2"}>
      <JobStatusIcon status={status} hasErrors={hasErrors} />
      {getJobStatusTextShort(status, hasErrors)}
    </div>
  );
};
